import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges, inject } from '@angular/core';
import {cloneDeep} from "lodash";
import {takeUntil} from "rxjs/operators";
import {SortDirectiveService} from "./sort-directive.service";
import {Subject} from "rxjs";


@Directive({
  selector: '[appSort]',
  standalone: true,
})
/**
 * Example 1:
 * <pre>
 * <tr id="all-organizations-table-header">
 *   <th class="size-xs" appSort
 *      [arr]="[orgs]"
 *      [keySelector]="byClient"
 *      [isActivated]="true"
 *      [order]="'desc'">
 *         Client
 *    </th>
 * </tr>
 * </pre>
 * * Example 2:
 * <pre>
 * <tr id="pp-summary-table">
 *   <th class="size-xs" appSort [arr]="[portfolioActive, portfolioSold]" [keySelector]="byClient">Client</th>
 * </tr>
 * </pre>
 *
 * 1) Add an id for the parent HTML elements. In this case it is \<tr\>.
 * 2) Add appsort, which is the reference to this directive.
 * 3) Add [arr] which is the array of array where internal arrays, would be sorted after the button click.
 * 4) Add [keySelector] which is an anonymous function, which takes the object and returns its field.
 * 5) (Optional) Add [isActivated] to sort array by default.
 * 6) (Optional) Add [order] to set the sorting direction.
 */
export class SortDirective<K, V> implements AfterViewInit, OnDestroy, OnChanges {
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);
  private sortService = inject(SortDirectiveService);

  private _destroyStream: Subject<void> = new Subject<void>();

  /**
   * Is needed to hide all icons in the row, in case other sort column was selected.<br>
   * See @{@link SortDirectiveService} for details.
   */
  private parentId: string;

  /**
   * Is the initial data of the cell. It can be preferably just the text,
   * but there is a medium support for the whole div classes. (might be problems with the positioning after the click).
   * Is needed to build the html structure with the icons.
   */
  private initialHtmlTemplate: string[];

  /**
   * Is the first "order", which is defined in the html. Is needed for the factoryReset().
   */
  private initialOrder: SortOrder;

  /**
   * This flag helps in distinguishing whether the current column is the one triggering the sort operation.
   * It's particularly useful in {@link SortDirective#initSubscriptions}, where we need to check if
   * the column's parent element (table row) matches the `tableID`. If `isCurrentlySorting` is `true`,
   * which means this column is the one being actively sorted, preventing unnecessary resetting of its state.
   */
  private isCurrentlySorting = false;

  /**
   * Stores the original, unsorted state of the input arrays.
   * Used to restore the arrays to their initial order when the sort is reset to 'initial'.
   *
   * Instead of storing actual values, indices are kept to ensure the original order is preserved,
   * even if the objects in the arrays change over time.
   *
   * Example:
   * Suppose we have an array of objects representing our beloved ones:
   *
   * this.arr = [
   *   [{ name: 'Dimas' }, { name: 'Zozobra' }, { name: 'Khomich' }, { name: 'Andryusha' }]
   * ];
   *
   * After storing the original state:
   *
   * this.originalArrayIndices = [
   *   [{ item: { name: 'Dimas' }, index: 0 }, { item: { name: 'Zozobra' }, index: 1 },
   *    { item: { name: 'Khomich' }, index: 2 }, { item: { name: 'Andryusha' }, index: 3 }]
   * ];
   *
   * Imagine 'Andryusha' becomes 'Lisenok' just like a werewolf does:
   *
   * this.arr = [
   *   [{ name: 'Dimas' }, { name: 'Zozobra' }, { name: 'Khomich' }, { name: 'Lisenok' }]
   * ];
   *
   * After sorting by name:
   *
   * this.arr = [
   *   [{ name: 'Dimas' }, { name: 'Khomich' }, { name: 'Lisenok' }, { name: 'Zozobra' }]
   * ];
   *
   * Resetting to the original order using stored indices results in:
   *
   * this.arr = [
   *   [{ name: 'Dimas' }, { name: 'Zozobra' }, { name: 'Khomich' }, { name: 'Lisenok' }]
   * ];
   *
   * Note: Using `cloneDeep` on the original `arr` would revert 'Lisenok' back to 'Andryusha', losing changes.
   * Indices are stored to preserve the latest state while restoring the original order.
   */
  private originalArrayIndices: Array<{item: V; index: number}[]> = [];

  /**
   * Is the generic array of arrays, where internal arrays will be sorted in this class.
   */
  @Input() arr: Array<Array<V>>;

  /**
   * Is the typed 😍 anon function, which takes an entry from the 'arr' and returns their property (field).
   *  Example:
   * <pre>
   *   byClient: (o: Organization) => o.client.name,
   * </pre>
   */
  @Input() keySelector!: (x: V) => K;

  @Input() order: SortOrder = 'initial';

  /**
   * This variable applies sorting after init.
   */
  @Input() isActivated = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.arr) {
      if (!changes.arr.firstChange) {
        // do not run on start, but after the new data has arrived.
        this.factoryReset();
      }
      // update the original array state to the latest provided
      this.storeOriginalIndices();
    }
    // do not run on start, but after the order was changed => used IR for dynamic changes
    // do not run on start, but after the new isActivated was changed => used IR for dynamic changes
    if (
      (changes.isActivated && !changes.isActivated.firstChange) ||
      (changes.order && !changes.order.firstChange)
    ) {
      this.initialOrder = this.order;
      this.factoryReset();
    }
  }

  ngAfterViewInit(): void {
    this.initVariables();
    this.initSubscriptions();
    this.initActions();
    this.renderer.addClass(this.el.nativeElement, 'clickable');
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  @HostListener('click')
  onSortClick(): void {
    this.updateOrder();
    this.sortData();
  }

  private sortData(): void {
    if (!this.keySelector || !this.arr) {
      return;
    }
    this.isCurrentlySorting = true;
    this.sortService.notifyOther(this.parentId);
    this.sort();
    this.addSortIcons();
    this.isCurrentlySorting = false;
  }

  private initVariables(): void {
    this.parentId = this.el.nativeElement.parentElement.id;
    this.initialHtmlTemplate = Array.from(this.el.nativeElement.childNodes as string[]);
    this.initialOrder = cloneDeep(this.order);
  }

  private initSubscriptions(): void {
    this.sortService.notify$.pipe(takeUntil(this._destroyStream)).subscribe((tableID: string) => {
      if (tableID === this.parentId && !this.isCurrentlySorting) {
        this.refreshIcons();
      }
    });
  }

  /**
   * After the data has been updated, sets the sorting to the initial state:
   *
   * 1) Removes the sort icon.
   * 2) In case it is sorted by default, sort it with the initial order and show the icon.
   */
  private factoryReset(): void {
    if (this.isActivated) {
      this.order = cloneDeep(this.initialOrder);
      this.sortData();
    } else {
      // Ensure the sort icon is shown even if not activated
      this.refreshIcons();
    }
  }

  private sort(): void {
    this.order === 'initial' ? this.restoreOriginalOrder() : this.sortWithNewOrder();
  }

  private storeOriginalIndices(): void {
    this.originalArrayIndices = this.arr.map((arrInstance) =>
      arrInstance?.map((item, index) => ({ item, index })),
    );
  }

  /**
   * This method iterates over each array within `this.arr` and reorders its elements
   * according to their original positions as stored in `this.originalArrayIndices`.
   *
   * It ensures that any sorting operations that have changed the order of the elements
   * can be reverted back to the initial order, while keeping any modifications to the
   * objects (e.g., name changes).
   */
  private restoreOriginalOrder(): void {
    this.arr.forEach((arrInstance, index) => {
      const originalOrder = this.originalArrayIndices[index];
      arrInstance.sort((a, b) => {
        const aIndex = originalOrder.findIndex((o) => o.item === a);
        const bIndex = originalOrder.findIndex((o) => o.item === b);
        return aIndex - bIndex;
      });
    });
  }

  private sortWithNewOrder(): void {
    const sort = new Sort<K, V>();
    this.arr.forEach((arrInstance) => {
      arrInstance.sort(sort.startSort(this.keySelector, this.order));
    });
  }

  private initActions(): void {
    this.addSortIcons();
    if (this.isActivated) {
      this.sortData();
    }
  }

  private refreshIcons(): void {
    this.order = 'initial';
    this.removeExistingSortIcons();
    this.addSortIcons();
  }

  private removeExistingSortIcons(): void {
    const oldSortingDiv = this.el.nativeElement.querySelector('[sorting-div="true"]');
    if (oldSortingDiv) {
      this.renderer.removeChild(this.el.nativeElement, oldSortingDiv);
    }
  }

  private addSortIcons(): void {
    const parentDiv = this.createParentDiv();
    const text = this.initialHtmlTemplate;
    const image = this.createImageWrapper();
    text.forEach((textPart) => this.renderer.appendChild(parentDiv, textPart));
    this.renderer.appendChild(parentDiv, image);
    this.el.nativeElement.textContent = '';
    this.renderer.appendChild(this.el.nativeElement, parentDiv);
  }

  private createParentDiv(): ElementRef {
    let parentDiv = this.el.nativeElement.querySelector('[parent-div="true"]');
    if (parentDiv) {
      this.renderer.removeChild(this.el.nativeElement, parentDiv);
    }
    parentDiv = this.renderer.createElement('div');
    this.renderer.addClass(parentDiv, 'flex');
    this.renderer.addClass(parentDiv, 'flex-row');
    this.renderer.addClass(parentDiv, 'items-center');
    this.renderer.addClass(parentDiv, 'justify-start');
    this.renderer.addClass(parentDiv, 'gap-[0.5rem]');
    this.renderer.addClass(parentDiv, 'clickable');
    this.renderer.setAttribute(parentDiv, 'parent-div', 'true');
    return parentDiv as ElementRef;
  }

  private createImageWrapper(): ElementRef {
    const imageDiv = this.createImageDiv();
    const img = this.createImage();
    this.renderer.appendChild(imageDiv, img);
    return imageDiv;
  }

  private createImageDiv(): ElementRef {
    const div = this.renderer.createElement('div');
    this.renderer.addClass(div, 'flex');
    this.renderer.addClass(div, 'flex-col');
    this.renderer.addClass(div, 'shrink-0');
    this.renderer.setAttribute(div, 'sorting-div', 'true');
    return div as ElementRef;
  }

  private createImage(): ElementRef {
    const iconName = this.getIconName();
    const basePath = '/assets/svg/common/';
    const img = this.renderer.createElement('img');

    this.renderer.addClass(img, 'h-[0.78125rem]');
    this.renderer.setAttribute(img, 'src', `${basePath}${iconName}.svg`);
    return img as ElementRef;
  }

  private getIconName(): string {
    switch (this.order) {
      case 'initial':
        return 'arrow-neutral';
      case 'asc':
        return 'sort-up';
      case 'desc':
        return 'sort-down';
      default:
        return 'arrow-neutral';
    }
  }

  private updateOrder(): void {
    switch (this.order) {
      case 'initial':
        this.order = 'desc';
        break;
      case 'desc':
        this.order = 'asc';
        break;
      case 'asc':
        this.order = 'initial';
        break;
      default:
        this.order = 'initial';
    }
  }
}

export class Sort<K, V> {
  private sortOrder: -1 | 1 = 1;

  public startSort(keySelector: (x: V) => K, order: SortOrder): (a: V, b: V) => number {
    if (order === 'desc') {
      this.sortOrder = -1;
    }
    return (a: V, b: V): number => {
      const keyA = keySelector(a);
      const keyB = keySelector(b);

      // Desc has nulls in the bottom. Asc has nulls at the top.
      if (keyA === null && keyB === null) {
        return 0;
      }
      if (keyA === null && keyB !== null) {
        return -this.sortOrder;
      }
      if (keyA !== null && keyB === null) {
        return this.sortOrder;
      }

      if (typeof keyA === 'string' && typeof keyB === 'string') {
        return keyA.localeCompare(keyB) * this.sortOrder;
      }
      if (typeof keyA === 'number' && typeof keyB === 'number') {
        return this.sortNumber(keyA, keyB);
      }
      if (typeof keyA === 'boolean' && typeof keyB === 'boolean') {
        if (keyA === keyB) {
          return 0;
        }
        return (keyA ? 1 : -1) * this.sortOrder;
      }
      if (typeof keyA === 'object' && typeof keyB === 'object') {
        // will be extended in the next feature iteration.
      }
      return 0;
    };
  }

  /**
   * In case the value is MIN_VALUE, it will be "excluded" from sorting,
   * and will always be placed in the end. E.x.
   * asc -> [40,-40, MIN_VALUE]
   * desc -> [-40,40, MIN_VALUE]
   *
   * @param keyA of the first sorting param.
   * @param keyB of the second sorting param.
   * @return either negative, 0 or positive number. Those 3 classifications determine the sorting.
   */
  private sortNumber(keyA: number, keyB: number): number {
    if (keyA === Number.MIN_VALUE && keyB === Number.MIN_VALUE) {
      return 0;
    }
    if (keyA === Number.MIN_VALUE) {
      return 1; // Always sort MIN_VALUE to the bottom
    }
    if (keyB === Number.MIN_VALUE) {
      return -1; // Always sort MIN_VALUE to the bottom
    }
    return (keyA - keyB) * this.sortOrder;
  }
}

export type SortOrder = 'initial' | 'asc' | 'desc';
